'use client';

import { c as _c } from "react/compiler-runtime";
import { useAccountEmailOtpStart, useAccountEmailOtpVerify } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { type ChangeEvent, type FormEvent, type ReactNode, useEffect, useReducer, useRef, useState } from 'react';
import { BANNER_CTA_BASE } from '@/components/layout/demo-mode-banner';
import { AnimatedButton } from '@/components/ui/animated-button';
import { InputField } from '@/components/ui/input-field';
import { OtpField } from '@/components/ui/otp-field';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { pickApiCode } from '@/lib/errors/resolve-api-error';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
import { EMAIL_REGEX } from '@/lib/validation/email';
const OTP_RESEND_COOLDOWN_MS = 60_000;

/**
 * "Add your email to claim rewards" — blue strip in the style of the
 * demo-mode banner, shown on the rewards dashboard to accounts without a
 * verified email. Opens the capture modal.
 */
export function EmailCaptureBanner(t0) {
  const $ = _c(7);
  const {
    onOpen
  } = t0;
  const t = useTranslations("rewards");
  let t1;
  if ($[0] !== onOpen || $[1] !== t) {
    let t2;
    if ($[3] !== onOpen) {
      t2 = chunks => <AnimatedButton variant="tertiary" size="xs" disableHoverScale={true} onClick={() => {
        onEvent("rewards_email_capture_opened", {});
        onOpen();
      }} className={cn(BANNER_CTA_BASE, "text-white hover:text-white hover:opacity-80 focus-visible:ring-white/40")}>{chunks}</AnimatedButton>;
      $[3] = onOpen;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = t.rich("emailBanner.message", {
      cta: t2
    });
    $[0] = onOpen;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="relative z-sticky w-full bg-[var(--brand-600)] px-4 py-2 text-center text-xs font-medium text-white dark:bg-[var(--brand-400)]">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
type CaptureStep = 'email' | 'code';
type CaptureStatus = 'idle' | 'loading' | 'error';

/** The OTP flow's related state slices — they transition together, so they live in one reducer. */
interface CaptureForm {
  step: CaptureStep;
  otp: string;
  status: CaptureStatus;
  /** Which request `loading` belongs to: verify and resend share the code step. */
  inFlight: 'send' | 'resend' | 'verify' | null;
  error: string | undefined;
  resendReadyAt: number;
}
type CaptureAction = {
  type: 'reset';
} | {
  type: 'submitted';
  inFlight: 'send' | 'resend' | 'verify';
} | {
  type: 'codeSent';
  resendReadyAt: number;
} | {
  type: 'failed';
  message: string;
} | {
  type: 'codeRejected';
  message: string;
} | {
  type: 'otpChanged';
  value: string;
} | {
  type: 'errorCleared';
};
const INITIAL_FORM: CaptureForm = {
  step: 'email',
  otp: '',
  status: 'idle',
  inFlight: null,
  error: undefined,
  resendReadyAt: 0
};
function captureReducer(form: CaptureForm, action: CaptureAction): CaptureForm {
  switch (action.type) {
    case 'reset':
      // The resend cooldown survives a reset so "change email" can't bypass it.
      return {
        ...INITIAL_FORM,
        resendReadyAt: form.resendReadyAt
      };
    case 'submitted':
      return {
        ...form,
        status: 'loading',
        inFlight: action.inFlight,
        error: undefined
      };
    case 'codeSent':
      return {
        ...form,
        step: 'code',
        otp: '',
        status: 'idle',
        resendReadyAt: action.resendReadyAt
      };
    case 'failed':
      return {
        ...form,
        status: 'error',
        error: action.message
      };
    case 'codeRejected':
      return {
        ...form,
        otp: '',
        status: 'error',
        error: action.message
      };
    case 'otpChanged':
      return form.status === 'error' ? {
        ...form,
        otp: action.value,
        status: 'idle',
        error: undefined
      } : {
        ...form,
        otp: action.value
      };
    case 'errorCleared':
      return form.status === 'error' ? {
        ...form,
        status: 'idle',
        error: undefined
      } : form;
  }
}

/** Verify the account email; the account emailVerified field hides the reminder banner. */
export function EmailCaptureModal(t0) {
  const $ = _c(49);
  const {
    open,
    onOpenChange,
    onVerified
  } = t0;
  const t = useTranslations("rewards");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      meta: {
        skipGlobalErrorHandler: true
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    mutateAsync: otpStartAsync
  } = useAccountEmailOtpStart(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      meta: {
        skipGlobalErrorHandler: true
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    mutateAsync: otpVerifyAsync
  } = useAccountEmailOtpVerify(t2);
  const [form, dispatch] = useReducer(captureReducer, INITIAL_FORM);
  const verifying = form.status === "loading" && form.inFlight === "verify";
  const [email, setEmail] = useState("");
  const [now, setNow] = useState(_temp);
  const otpInputRef = useRef(null);
  let t3;
  let t4;
  if ($[2] !== form.resendReadyAt || $[3] !== form.step || $[4] !== now) {
    t3 = () => {
      if (form.step !== "code" || form.resendReadyAt <= now) {
        return;
      }
      const id = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(id);
    };
    t4 = [form.step, form.resendReadyAt, now];
    $[2] = form.resendReadyAt;
    $[3] = form.step;
    $[4] = now;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = function resetForm() {
      dispatch({
        type: "reset"
      });
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const resetForm = t5;
  let t6;
  if ($[8] !== onOpenChange) {
    t6 = function resetAndClose(nextOpen) {
      if (!nextOpen) {
        resetForm();
      }
      onOpenChange(nextOpen);
    };
    $[8] = onOpenChange;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const resetAndClose = t6;
  let t7;
  if ($[10] !== t) {
    t7 = function startErrorMessage(code) {
      if (code === "NOTIFICATION_EMAIL_REJECTED") {
        return t("emailModal.errors.rejected");
      }
      if (code === "NOTIFICATION_OTP_COOLDOWN" || code === "NOTIFICATION_RATE_LIMITED") {
        return t("emailModal.errors.rateLimited");
      }
      if (code === "NOTIFICATION_OTP_SEND_LIMIT") {
        return t("emailModal.errors.sendLimit");
      }
      return t("emailModal.errors.server");
    };
    $[10] = t;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const startErrorMessage = t7;
  let t8;
  if ($[12] !== email || $[13] !== otpStartAsync || $[14] !== startErrorMessage || $[15] !== t) {
    t8 = function sendCode(opts) {
      const trimmed = email.trim();
      if (!EMAIL_REGEX.test(trimmed)) {
        dispatch({
          type: "failed",
          message: t("emailModal.errors.invalidEmail")
        });
        return;
      }
      dispatch({
        type: "submitted",
        inFlight: opts.resend ? "resend" : "send"
      });
      otpStartAsync({
        email: trimmed
      }).then(() => {
        dispatch({
          type: "codeSent",
          resendReadyAt: Date.now() + OTP_RESEND_COOLDOWN_MS
        });
        setNow(Date.now());
        if (opts.resend) {
          showToast({
            id: TOAST_IDS.ACCOUNT_EMAIL_OTP_RESENT,
            message: t("emailModal.toast.resent", {
              email: trimmed
            }),
            variant: "success"
          });
        }
      }).catch(err => {
        dispatch({
          type: "failed",
          message: startErrorMessage(pickApiCode(err))
        });
      });
    };
    $[12] = email;
    $[13] = otpStartAsync;
    $[14] = startErrorMessage;
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const sendCode = t8;
  let t9;
  if ($[17] !== email || $[18] !== form.status || $[19] !== onVerified || $[20] !== otpVerifyAsync || $[21] !== resetAndClose || $[22] !== t) {
    t9 = function verify(codeValue) {
      if (codeValue.length < 6 || form.status === "loading") {
        return;
      }
      dispatch({
        type: "submitted",
        inFlight: "verify"
      });
      otpVerifyAsync({
        email: email.trim(),
        code: codeValue
      }).then(() => {
        onEvent("rewards_email_verified", {});
        showToast({
          id: TOAST_IDS.REWARDS_EMAIL_VERIFIED,
          message: t("emailModal.toast.verified"),
          variant: "success"
        });
        onVerified();
        resetAndClose(false);
      }).catch(err_0 => {
        const code_0 = pickApiCode(err_0);
        const message = code_0 === "NOTIFICATION_OTP_INVALID" ? t("emailModal.errors.invalidCode") : code_0 === "NOTIFICATION_OTP_EXPIRED" ? t("emailModal.errors.expiredCode") : code_0 === "NOTIFICATION_OTP_TOO_MANY_ATTEMPTS" ? t("emailModal.errors.tooManyAttempts") : t("emailModal.errors.server");
        dispatch({
          type: "codeRejected",
          message
        });
        otpInputRef.current?.focus();
      });
    };
    $[17] = email;
    $[18] = form.status;
    $[19] = onVerified;
    $[20] = otpVerifyAsync;
    $[21] = resetAndClose;
    $[22] = t;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const verify = t9;
  let t10;
  if ($[24] !== sendCode) {
    t10 = function onSubmitEmail(e) {
      e.preventDefault();
      sendCode({
        resend: false
      });
    };
    $[24] = sendCode;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const onSubmitEmail = t10;
  const resendSecondsLeft = Math.max(0, Math.ceil((form.resendReadyAt - now) / 1000));
  let t11;
  if ($[26] !== t) {
    t11 = t("emailModal.title");
    $[26] = t;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t) {
    t12 = t("emailModal.description");
    $[28] = t;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== email || $[31] !== form.error || $[32] !== form.inFlight || $[33] !== form.otp || $[34] !== form.status || $[35] !== form.step || $[36] !== onSubmitEmail || $[37] !== resendSecondsLeft || $[38] !== sendCode || $[39] !== t || $[40] !== verify || $[41] !== verifying) {
    t13 = form.step === "email" ? <form onSubmit={onSubmitEmail} className="flex flex-col gap-4"><InputField type="email" inputMode="email" autoComplete="email" label={t("emailModal.emailLabel")} placeholder={t("emailModal.emailPlaceholder")} value={email} onChange={e_0 => {
        setEmail(e_0.target.value);
        dispatch({
          type: "errorCleared"
        });
      }} error={form.status === "error" ? form.error : undefined} autoFocus={true} /><AnimatedButton type="submit" variant="primary" disabled={form.status === "loading" || email.trim() === ""} loading={form.status === "loading"}>{form.status === "loading" ? t("emailModal.sendingCode") : t("emailModal.sendCode")}</AnimatedButton></form> : <div className="flex flex-col gap-4"><p className="text-body text-foreground-secondary">{t("emailModal.sentTo", {
          email: email.trim()
        })}</p><OtpField value={form.otp} onChange={v => {
        dispatch({
          type: "otpChanged",
          value: v
        });
      }} onComplete={verify} disabled={form.status === "loading"} error={form.status === "error"} autoFocus={true} inputRef={otpInputRef} aria-label={t("emailModal.codeAriaLabel")} />{form.error !== undefined && <p className="text-caption text-feedback-error-content">{form.error}</p>}<AnimatedButton variant="primary" disabled={form.status === "loading" || form.otp.length < 6} loading={verifying} onClick={() => verify(form.otp)}>{verifying ? t("emailModal.verifying") : t("emailModal.verify")}</AnimatedButton><div className="flex items-center justify-between"><AnimatedButton variant="tertiary" size="xs" onClick={resetForm}>{t("emailModal.changeEmail")}</AnimatedButton><AnimatedButton variant="tertiary" size="xs" disabled={resendSecondsLeft > 0 || form.status === "loading"} loading={form.status === "loading" && form.inFlight === "resend"} onClick={() => sendCode({
          resend: true
        })}>{resendSecondsLeft > 0 ? t("emailModal.resendIn", {
            seconds: resendSecondsLeft
          }) : t("emailModal.resend")}</AnimatedButton></div></div>;
    $[30] = email;
    $[31] = form.error;
    $[32] = form.inFlight;
    $[33] = form.otp;
    $[34] = form.status;
    $[35] = form.step;
    $[36] = onSubmitEmail;
    $[37] = resendSecondsLeft;
    $[38] = sendCode;
    $[39] = t;
    $[40] = verify;
    $[41] = verifying;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== open || $[44] !== resetAndClose || $[45] !== t11 || $[46] !== t12 || $[47] !== t13) {
    t14 = <SilkSheet open={open} onOpenChange={resetAndClose} variant="detached" title={t11} description={t12}>{t13}</SilkSheet>;
    $[43] = open;
    $[44] = resetAndClose;
    $[45] = t11;
    $[46] = t12;
    $[47] = t13;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  return t14;
}
function _temp() {
  return Date.now();
}